import { modals } from "@mantine/modals";
import DataTable from "../components/common/DataTable";
import { Button, Modal, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import React from "react";
import Head from "../components/common/Head";
import useValidate from "../hooks/useValidate";
import {
  addGenre,
  deleteMethod,
  getAllGenres,
  updateGenre,
} from "../utils/api";
import useStore from "../utils/store";

const Genres = () => {
  const [modal, setModal] = React.useState(false);
  const [genres, setGenres] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [buttonLoading, setButtonLoading] = React.useState(false);
  const [newGenre, setNewGenre] = React.useState({});
  const [searchText, setSearchText] = React.useState("");
  const user = useStore((state) => state.user);

  const getAllGenresData = async () => {
    if (!user?.getAllGenres) return;
    setLoading(true);
    const [success, response] = await getAllGenres();
    if (success) setGenres(response);
    setLoading(false);
  };

  const filteredGenres = React.useMemo(() => {
    if (!searchText) return genres;
    return genres.filter((genre) =>
      genre.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [genres, searchText]);

  const addNewGenre = async () => {
    if (!newGenre.name) return;
    setButtonLoading(true);
    const [success] = newGenre?._id
      ? await updateGenre(newGenre?._id, newGenre)
      : await addGenre(newGenre);
    if (success) {
      notifications.show({
        message: newGenre?._id ? "Genre updated successfully" : "Genre added successfully",
        color: "green",
      });
      setModal(false);
      setNewGenre({});
      getAllGenresData();
    }
    setButtonLoading(false);
  };

  const showDeleteConfirm = (id, name) => {
    modals.openConfirmModal({
      title: `Delete genre "${name}"?`,
      children: "This action cannot be undone.",
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        const [success] = await deleteMethod(`/admin/genre/${id}`);
        if (success) {
          notifications.show({ message: "Genre deleted successfully", color: "green" });
          getAllGenresData();
        } else {
          notifications.show({ message: "Failed to delete genre", color: "red" });
        }
      },
    });
  };

  useValidate();
  React.useEffect(() => {
    getAllGenresData();
  }, []);

  return (
    <div className="table-page">
      <Head
        title="Genres"
        button={user?.addGenre ? "Add Genre" : null}
        onClick={() => setModal(true)}
      />
      <Modal
        opened={modal}
        onClose={() => setModal(false)}
        title={newGenre?._id ? "Edit Genre" : "Add Genre"}
      >
        <TextInput
          label="Genre Name"
          placeholder="Enter genre name"
          value={newGenre.name || ""}
          onChange={(e) => setNewGenre({ ...newGenre, name: e.target.value })}
          mb="md"
        />
        <div className="form-card-actions">
          <Button onClick={addNewGenre} loading={buttonLoading}>
            {newGenre?._id ? "Update" : "Add"}
          </Button>
          <Button variant="default" onClick={() => setModal(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
      <div className="table-page-search">
        <TextInput
          placeholder="Search genres..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      {user?.getAllGenres ? (
        <DataTable
          loading={loading}
          dataSource={filteredGenres}
          columns={[
            {
              title: "Genre Name",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Actions",
              key: "actions",
              render: (text, record) => (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {user?.editGenre && (
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() => {
                        setNewGenre(record);
                        setModal(true);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  {user?.deleteGenre && (
                    <Button
                      color="red"
                      variant="light"
                      size="xs"
                      onClick={() => showDeleteConfirm(record._id, record.name)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      ) : (
        <div className="table-card table-card-empty">
          You don&apos;t have permission to view genres
        </div>
      )}
    </div>
  );
};

export default Genres;
